import React, { useState, useEffect, useRef } from "react"
import { PencilIcon, FolderIcon, XMarkIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Input from "aldoo-ra/Input"
import Modal from "aldoo-ra/Modal"
import ContentEditor from "aldoo-ra/CMS/content-editor"
import Typography from "aldoo-ra/Typography"
import { getApi } from "aldoo-ra/CMS/field-api-resolver"
import { showNotification } from "aldoo-ra/Notification"
import { getFormatter } from "aldoo-ra/CMS/field-formatter"
import { getDisplayFormatter } from "aldoo-ra/CMS/field-display-format-resolver"
import DatabaseModelSelectDialog from "aldoo-ra/CMS/dialogs/database-model-select-dialog"
import DatabaseModelTypeResolver from "aldoo-ra/CMS/database-model-type-resolver"
import { getCustomAction } from "aldoo-ra/CMS/custom-action-resolver"
import _ from "lodash"

function DatabaseModelEditor({
  onChange,
  value,
  field,
  parentValues,
  parentObject,
  save,
}) {
  const [showDialog, setShowDialog] = useState(false)
  const [selectContentDialog, setSelectContentDialog] = useState(false)
  const [localValue, setLocalValue] = useState(value || null)
  const [fieldType, setFieldType] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [editingTitle, setEditingTitle] = useState(false)
  const [tempPriceId, setTempPriceId] = useState('')

  // Add ref to track if the change is internal
  const isInternalChange = useRef(false)

  const { getType } = DatabaseModelTypeResolver()

  // Note: Removed debug logging for parentObject changes

  // Set field type on mount and when field changes
  useEffect(() => {
    try {
      if (typeof field.item === "string") {
        const resolvedType = getType(field.item)
        if (!resolvedType) {
          throw new Error(`Could not resolve type for ${field.item}`)
        }
        setFieldType(resolvedType)
      } else {
        setFieldType(field.item)
      }
    } catch (err) {
      setError(err.message)
      showNotification({
        message: `Error resolving field type: ${err.message}`,
        className: "bg-admin_error text-white",
      })
    }
  }, [field, getType])

  // Handle external value changes
  useEffect(() => {
    if (!isInternalChange.current && !_.isEqual(value, localValue)) {
      setLocalValue(value || null)
    }
    isInternalChange.current = false
  }, [value])

  // Unified function to update value and notify parent
  const updateValue = (newValue) => {
    isInternalChange.current = true
    setLocalValue(newValue)
    onChange?.(newValue)
  }

  const isRequired =
    typeof field.required === "function"
      ? field.required(parentObject)
      : field.required

  const handleCloseDialog = () => {
    setShowDialog(false)
    setError(null)
    setEditingTitle(false)
    setTempPriceId('')
  }

  const handleEditClick = async () => {
    const actualParentObject = typeof parentObject === 'function' ? parentObject() : parentObject

    // Check if there's a custom edit handler registered for this field
    if (field.render?.customEditHandler) {
      // Get the parent type name to construct action key
      const parentType = actualParentObject?._contentType

      if (parentType) {
        const actionKey = `${parentType}.${field.id}.${field.render.customEditHandler}`
        const customAction = getCustomAction(actionKey)

        if (customAction) {
          // Get the actual parent object (call if function, use directly if object)
          const actualParentObject = typeof parentObject === 'function' ? parentObject() : parentObject
          const actualParentValues = typeof parentValues === 'function' ? parentValues() : parentValues

          // Call the custom action with the parent object and save function
          const result = await customAction(actualParentObject || actualParentValues, save)
          // If action returns false, don't proceed with default behavior
          if (result === false) {
            return
          }
        }
      }
    }

    // If no value exists and autoCreateEmpty is enabled, create via CRUD API
    if (!localValue && field.render?.autoCreateEmpty) {
      setIsLoading(true)
      try {
        const api = getApi(fieldType.type)
        if (!api) {
          throw new Error(`API not found for ${fieldType.type}`)
        }

        // Create empty object via CRUD API
        const emptyObject = typeof field.render.autoCreateEmpty === 'object'
          ? field.render.autoCreateEmpty
          : {}

        const createResponse = await api.Save(emptyObject)
        if (createResponse.error) {
          throw new Error(createResponse.error)
        }

        if (createResponse.result) {
          // Fetch the created object
          const [created] = await api.Get({
            match: { _id: createResponse.result },
          })
          if (!created) {
            throw new Error("Failed to fetch created model")
          }
          updateValue(created)
        }
      } catch (err) {
        showNotification({
          message: `Failed to create ${fieldType.label}: ${err.message}`,
          className: "bg-admin_error text-white",
        })
        setIsLoading(false)
        return // Don't open dialog if creation failed
      } finally {
        setIsLoading(false)
      }
    }
    setShowDialog(true)
  }

  // Find hidden title field (a required field that's hidden)
  const getTitleField = () => {
    if (!fieldType?.fields) return null

    // Find a field that is required, hidden, and type Text
    return fieldType.fields.find(f =>
      f.required &&
      typeof f.visibleIf === 'function' &&
      f.visibleIf() === false &&
      f.type === 'Text'
    )
  }

  // Initialize tempPriceId when dialog opens
  useEffect(() => {
    if (showDialog && fieldType) {
      const titleField = getTitleField()
      if (titleField) {
        // Set tempPriceId from localValue if it exists, otherwise set empty string
        setTempPriceId(localValue?.[titleField.id] || '')
        // Start in editing mode if creating new item (no localValue)
        setEditingTitle(!localValue)
      }
    }
  }, [showDialog, fieldType, localValue])

  const handleSaveModel = async (newValues) => {
    setIsLoading(true)
    setError(null)

    try {
      const api = getApi(fieldType.type)
      if (!api) {
        throw new Error(`API not found for ${fieldType.type}`)
      }

      // Include edited title field if it was modified or if creating new item
      const titleField = getTitleField()
      const titleFieldUpdate = titleField && tempPriceId && (tempPriceId !== localValue?.[titleField.id] || !localValue)
        ? { [titleField.id]: tempPriceId }
        : {}

      // Validate that required title field has a value when creating new item
      if (titleField && titleField.required && !localValue && (!tempPriceId || tempPriceId.trim() === '')) {
        throw new Error(`${titleField.label} is required`)
      }

      const updatedValues = {
        ...newValues,
        _id: localValue?._id,
        ...titleFieldUpdate
      }

      const saveResponse = await api.Save(updatedValues)
      if (saveResponse.error) {
        throw new Error(saveResponse.error)
      }

      if (saveResponse.result) {
        try {
          const [updated] = await api.Get({
            match: { _id: saveResponse.result },
          })
          if (!updated) {
            throw new Error("Failed to fetch updated model")
          }
          updateValue(updated)
        } catch (fetchError) {
          throw new Error(
            `Failed to fetch updated model: ${fetchError.message}`
          )
        }
      }

      showNotification({
        message: `${fieldType.type} ${
          !updatedValues._id ? "created" : "updated"
        } successfully`,
        className: "bg-admin_primary text-white",
      })
      // Dialog will be closed by onClickSave callback after this function returns
      return saveResponse
    } catch (err) {
      setError(err.message)
      showNotification({
        message: err.message,
        className: "bg-admin_error text-white",
      })
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const handleModelSelect = (selectedModel) => {
    updateValue(selectedModel)
    setSelectContentDialog(false)
  }

  const clearModel = () => {
    updateValue(null)
  }

  const overview = getDisplayFormatter(field?.overview || fieldType?.type)

  return (
    <div className="database-model-editor w-full">
      <div className="flex flex-col space-y-2">
        <div className="flex items-center justify-between p-4 border border-admin_bordered rounded-lg bg-admin_paper">
          <button
            type="button"
            onClick={handleEditClick}
            disabled={isLoading}
            className="flex-1 text-left text-admin_text hover:text-admin_primary origin-left transition-all duration-150 hover:scale-105"
          >
            <Typography>
              {localValue ? overview && overview(localValue) : `Edit ${field.label?.replace(' Configuration', '') || 'Model'}`}
            </Typography>
          </button>

          <Button
            variant="admin_secondary"
            size="sm"
            className="ml-4"
            onClick={handleEditClick}
            disabled={isLoading}
          >
            <PencilIcon className="w-6 h-6 mr-2" />
          </Button>

          {!field.render?.hideSelectButton && (
            <Button
              variant="admin_secondary"
              size="sm"
              className="ml-4"
              onClick={() => setSelectContentDialog(true)}
              disabled={isLoading}
            >
              <FolderIcon className="w-6 h-6 mr-2" />
            </Button>
          )}

          {!field.render?.hideClearButton && localValue && (
            <Button
              variant="admin_secondary"
              size="sm"
              className="ml-4"
              onClick={clearModel}
              disabled={isLoading}
              title="Clear selection"
            >
              <XMarkIcon className="w-6 h-6" />
            </Button>
          )}
        </div>

        {error && <div className="text-sm text-error mt-1">{error}</div>}
      </div>

      {showDialog && fieldType && (
        <Modal
          variant="admin"
          isOpen={showDialog}
          onClose={handleCloseDialog}
          closeOnEscape
          className="md:absolute md:top-[65px] w-full sm:w-[500px] md:w-[80%] h-full md:h-auto pb-10"
        >
          <ContentEditor
            title={
              (() => {
                const titleField = getTitleField()

                // If no title field, use parent product name or field label
                if (!titleField) {
                  // For price field, use parent product name
                  if (field.id === 'price' && parentValues?.name) {
                    const productName = typeof parentValues.name === 'object'
                      ? (parentValues.name.en || parentValues.name.default || Object.values(parentValues.name)[0])
                      : parentValues.name
                    return productName || field.label?.replace('Store ', '') || 'Edit'
                  }
                  return field.label?.replace('Store ', '') || 'Edit'
                }

                return editingTitle ? (
                  <Input
                    value={tempPriceId}
                    onChange={(e) => setTempPriceId(e.target.value)}
                    onBlur={() => {
                      // Validate on blur using field's required property
                      if (titleField.required && (!tempPriceId || tempPriceId.trim() === '')) {
                        showNotification({
                          message: `${titleField.label} cannot be empty`,
                          className: 'bg-admin_error text-white'
                        })
                        // Restore original value
                        setTempPriceId(localValue?.[titleField.id] || '')
                      }
                      setEditingTitle(false)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.target.blur()
                      }
                      if (e.key === 'Escape') {
                        // Restore original value on escape
                        setTempPriceId(localValue?.[titleField.id] || '')
                        setEditingTitle(false)
                      }
                    }}
                    autoFocus
                    variant="admin"
                  />
                ) : (
                  <div
                    className="flex items-center gap-3 cursor-pointer group"
                    onClick={() => setEditingTitle(true)}
                  >
                    <span className="text-admin_text text-2xl font-bold">
                      {tempPriceId || `Untitled ${fieldType.label || 'Item'}`}
                    </span>
                    <PencilIcon className="w-6 h-6 text-admin_text_secondary group-hover:text-admin_text transition-colors" />
                  </div>
                )
              })()
            }
            contentType={fieldType}
            dataItem={localValue}
            parentValues={parentValues}
            parentObject={parentObject}
            fieldValues={localValue}
            formatters={[getFormatter(fieldType?.type)]}
            onSave={handleSaveModel}
            onClickSave={(result) => {
              // Only close dialog if save was successful
              if (result) {
                handleCloseDialog()
              }
            }}
            onError={(errors) => {
              const errorMessage = Array.isArray(errors)
                ? errors.join(", ")
                : "Validation failed. Please check the form."
              setError(errorMessage)
              showNotification({
                message: errorMessage,
                className: "bg-admin_error text-white",
              })
            }}
            isLoading={isLoading}
            showSearch={false}
            showExport={true}
            showCopyPaste={true}
          />
        </Modal>
      )}

      {selectContentDialog && fieldType && (
        <DatabaseModelSelectDialog
          type={fieldType?.type}
          isOpen={selectContentDialog}
          onClose={() => setSelectContentDialog(false)}
          searchFields={field.searchFields}
          overview={getDisplayFormatter(field.overview)}
          onSelect={handleModelSelect}
          disabled={isLoading}
        />
      )}
    </div>
  )
}

export default DatabaseModelEditor
